import React, { useState } from 'react';
import { useListings } from '@/hooks/useListings';
import { Sidebar } from '@/components/Sidebar';
import { NetSheetEditor } from '@/components/NetSheetEditor';
import { ScenarioData } from '@/lib/index';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Index() {
  const {
    listings,
    activeListing,
    activeId,
    setActiveId,
    loading,
    error,
    createListing,
    deleteListing,
    updateListingMeta,
    updateScenario,
  } = useListings();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleMetaChange = (field: 'sellerName' | 'property', value: string) => {
    if (activeListing) updateListingMeta(activeListing.id, { [field]: value });
  };

  const handleScenarioChange = (idx: 0 | 1 | 2, field: keyof ScenarioData, value: number | string) => {
    if (activeListing) updateScenario(activeListing.id, idx, field, value);
  };

  const handleDelete = () => {
    if (activeListing) deleteListing(activeListing.id);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile sidebar overlay — BELOW z-index of content */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - desktop always visible, mobile drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-30 md:static md:z-auto transition-transform duration-200 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <Sidebar
          listings={listings}
          activeId={activeId}
          onSelect={(id) => { setActiveId(id); setSidebarOpen(false); }}
          onCreate={() => { createListing(); setSidebarOpen(false); }}
          onDelete={deleteListing}
        />
      </div>

      {/* Main Content — full height, scrollable, NO fixed overlay on top */}
      <main className="flex-1 overflow-y-auto flex flex-col">
        {/* Mobile hamburger bar — INSIDE scroll flow, not fixed overlay */}
        <div className="md:hidden bg-card border-b border-border px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1.5">
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </Button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground leading-tight truncate">
              {activeListing?.sellerName || 'New Listing'}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {activeListing?.property || 'Seller Net Sheet'}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center flex-1 gap-3">
            <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <p className="text-sm text-muted-foreground">Loading listings from database…</p>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center flex-1">
            <div className="text-center space-y-2 p-6">
              <p className="text-sm font-semibold text-destructive">Connection error</p>
              <p className="text-xs text-muted-foreground">{error}</p>
            </div>
          </div>
        ) : activeListing ? (
          <NetSheetEditor
            listing={activeListing}
            onMetaChange={handleMetaChange}
            onScenarioChange={handleScenarioChange}
            onDelete={handleDelete}
          />
        ) : (
          <div className="flex items-center justify-center flex-1">
            <p className="text-muted-foreground text-sm">Select or create a listing to get started.</p>
          </div>
        )}
      </main>
    </div>
  );
}
